import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function SiteEquipmentTab({ siteId, siteName }) {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    equipment_ref: '',
    equipment_type: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    installation_date: '',
    warranty_expiry: '',
    location_description: '',
    notes: ''
  });

  useEffect(() => {
    fetchEquipment();
  }, [siteId]);

  const fetchEquipment = async () => {
    try {
      const response = await apiFetch(`/api/sites/${siteId}/equipment`);
      const data = await response.json();
      setEquipment(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      equipment_ref: '',
      equipment_type: '',
      manufacturer: '',
      model: '',
      serial_number: '',
      installation_date: '',
      warranty_expiry: '',
      location_description: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      equipment_ref: item.equipment_ref || '',
      equipment_type: item.equipment_type || '',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      installation_date: item.installation_date ? item.installation_date.split('T')[0] : '',
      warranty_expiry: item.warranty_expiry ? item.warranty_expiry.split('T')[0] : '',
      location_description: item.location_description || '',
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingItem
        ? `/api/equipment/${editingItem.id}`
        : '/api/equipment';

      const payload = editingItem
        ? { ...formData, is_active: true }
        : { ...formData, site_id: siteId };

      const response = await apiFetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowModal(false);
        fetchEquipment();
      } else {
        alert('Error saving equipment');
      }
    } catch (error) {
      console.error('Error saving equipment:', error);
      alert('Error saving equipment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Mark this door or gate as removed? The record stays on past jobs.')) return;

    try {
      await apiFetch(`/api/equipment/${id}`, {
        method: 'DELETE'
      });
      fetchEquipment();
    } catch (error) {
      console.error('Error deleting equipment:', error);
      alert('Error deleting equipment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Fetch equipment types from admin custom-lists (door_type)
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await apiFetch('/api/custom-lists?list_type=door_type');
        const data = await res.json();
        setEquipmentTypes(data.map(item => item.list_value));
      } catch (err) {
        console.error('Error fetching equipment types:', err);
        // Fallback to basic list
        setEquipmentTypes(['automatic_door', 'roller_shutter', 'electric_gate', 'barrier', 'fire_door', 'manual_door']);
      }
    };
    fetchTypes();
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading equipment...</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Site Equipment</h2>
          <p className="text-sm text-gray-600 mt-1">{siteName}</p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium shadow-sm transition"
        >
          ➕ Add Equipment
        </button>
      </div>

      {equipment.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-6xl mb-4">🚪</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No equipment yet</h3>
          <p className="text-gray-500 mb-4">Add doors, gates, or other equipment to this site</p>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium"
          >
            Add First Equipment
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {equipment.map((item) => (
            <div
              key={item.id}
              className="p-5 bg-white border-2 border-gray-200 shadow-sm rounded-xl hover:border-green-300 transition"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900 text-lg">{item.equipment_type}</h3>
                    {item.equipment_ref && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-semibold">
                        {item.equipment_ref}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {item.location_description && (
                      <div>
                        <span className="text-gray-500">Location:</span>{' '}
                        <span className="text-gray-900 font-medium">{item.location_description}</span>
                      </div>
                    )}
                    {item.manufacturer && (
                      <div>
                        <span className="text-gray-500">Manufacturer:</span>{' '}
                        <span className="text-gray-900">{item.manufacturer}</span>
                      </div>
                    )}
                    {item.model && (
                      <div>
                        <span className="text-gray-500">Model:</span>{' '}
                        <span className="text-gray-900">{item.model}</span>
                      </div>
                    )}
                    {item.serial_number && (
                      <div>
                        <span className="text-gray-500">Serial:</span>{' '}
                        <span className="text-gray-900 font-mono text-xs">{item.serial_number}</span>
                      </div>
                    )}
                    {item.installation_date && (
                      <div>
                        <span className="text-gray-500">Installed:</span>{' '}
                        <span className="text-gray-900">{formatDate(item.installation_date)}</span>
                      </div>
                    )}
                    {item.warranty_expiry && (
                      <div>
                        <span className="text-gray-500">Warranty:</span>{' '}
                        <span className={`${new Date(item.warranty_expiry) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatDate(item.warranty_expiry)}
                        </span>
                      </div>
                    )}
                  </div>

                  {item.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                      <strong>Notes:</strong> {item.notes}
                    </div>
                  )}
                </div>

                <div className="ml-4 flex flex-col gap-2">
                  <button
                    onClick={() => navigate(`/equipment/${item.id}`)}
                    className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingItem ? 'Edit Equipment' : 'Add Equipment'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipment Type *
                  </label>
                  <select
                    value={formData.equipment_type}
                    onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    {equipmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipment Ref
                  </label>
                  <input
                    type="text"
                    value={formData.equipment_ref}
                    onChange={(e) => setFormData({ ...formData, equipment_ref: e.target.value })}
                    placeholder="EQ-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Description
                </label>
                <input
                  type="text"
                  value={formData.location_description}
                  onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                  placeholder="Main entrance, Loading bay 3, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                    placeholder="ASSA ABLOY, Hormann, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Installation Date
                  </label>
                  <input
                    type="date"
                    value={formData.installation_date}
                    onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warranty Expiry
                  </label>
                  <input
                    type="date"
                    value={formData.warranty_expiry}
                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Additional information..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SiteEquipmentTab;
